import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LoginRequest, SignupRequest, User } from '@madboat/types';
import * as authAPI from '@/api/auth';
import { identifyUser, resetUser } from '@/lib/analytics';

// Lazy import to avoid circular dependency (odisseiaStore imports authStore)
const getOdisseiaStore = () => import('./odisseiaStore').then(m => m.useOdisseiaStore.getState());

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;

  // Actions
  login: (data: LoginRequest) => Promise<void>;
  signup: (data: SignupRequest) => Promise<void>;
  setAuth: (token: string, user: User) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  sendHeartbeat: () => Promise<void>;
  clearError: () => void;
}

/**
 * Auth Store (Zustand)
 * Manages authentication state with persistence
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      loading: false,
      error: null,

      // Login action
      login: async (data) => {
        set({ loading: true, error: null });
        try {
          const response = await authAPI.login(data);
          set({
            user: response.user,
            token: response.token,
            loading: false,
            error: null,
          });
          identifyUser({
            id: response.user.id,
            email: response.user.email,
            name: response.user.name,
            level: response.user.level,
            xp: response.user.xp,
            role: response.user.role,
          });
          // Load Odisseia progress
          getOdisseiaStore().then(s => s.fetchProgress());
        } catch (error: any) {
          set({
            error: error.message || 'Login failed',
            loading: false,
          });
          throw error;
        }
      },

      // Signup action
      signup: async (data) => {
        set({ loading: true, error: null });
        try {
          const response = await authAPI.signup(data);
          set({
            user: response.user,
            token: response.token,
            loading: false,
            error: null,
          });
          identifyUser({
            id: response.user.id,
            email: response.user.email,
            name: response.user.name,
            level: response.user.level,
            xp: response.user.xp,
            role: response.user.role,
          });
          // Load Odisseia progress
          getOdisseiaStore().then(s => s.fetchProgress());
        } catch (error: any) {
          set({
            error: error.message || 'Signup failed',
            loading: false,
          });
          throw error;
        }
      },

      // Set auth directly (for invite redemption, external auth)
      setAuth: (token, user) => {
        set({
          user,
          token,
          loading: false,
          error: null,
        });
        identifyUser({
          id: user.id,
          email: user.email,
          name: user.name,
          level: user.level,
          xp: user.xp,
          role: user.role,
        });
        // Load Odisseia progress
        getOdisseiaStore().then(s => s.fetchProgress());
      },

      // Logout action
      logout: () => {
        resetUser();
        authAPI.logout();
        set({
          user: null,
          token: null,
          error: null,
        });
      },

      // Refresh user data (revalidate token)
      refreshUser: async () => {
        const { token } = get();
        if (!token) return;

        set({ loading: true });
        try {
          const response = await authAPI.getCurrentUser(token);
          set({
            user: response.user,
            loading: false,
          });
        } catch (error: any) {
          // Token invalid - logout
          set({
            user: null,
            token: null,
            loading: false,
            error: 'Session expired. Please login again.',
          });
        }
      },

      // Send heartbeat to keep session alive
      sendHeartbeat: async () => {
        const { token } = get();
        if (!token) return;

        try {
          await authAPI.sendHeartbeat(token);
        } catch (error: any) {
          console.error('[Auth] Heartbeat failed:', error);
          // If heartbeat fails, session might be expired
          // We don't logout automatically, refreshUser will handle it
        }
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'madboat-auth', // localStorage key
      partialize: (state) => ({
        user: state.user,
        token: state.token, // This is the session_id
      }), // Only persist user and token
      onRehydrateStorage: () => {
        console.log('[Auth] onRehydrateStorage called');
        return (hydratedState, error) => {
          if (error) {
            console.error('[Auth] Hydration error:', error);
          } else {
            console.log('[Auth] Rehydrated state:', hydratedState);
            if (hydratedState?.token && hydratedState?.user) {
              console.log('[Auth] Session found in storage, token:', hydratedState.token.substring(0, 8) + '...');
              identifyUser({
                id: hydratedState.user.id,
                email: hydratedState.user.email,
                name: hydratedState.user.name,
                level: hydratedState.user.level,
                xp: hydratedState.user.xp,
                role: hydratedState.user.role,
              });
              // Load Odisseia progress on rehydrate
              getOdisseiaStore().then(s => s.fetchProgress());
            } else {
              console.log('[Auth] No session found in storage');
            }
          }
        };
      },
    }
  )
);
